/** Connections, onboarding and the message log: types and API (docs/BRIEF-CONNECTIONS.md). */
import { api } from './api';

export interface ChannelState { connected: boolean; hint?: string | null; on: boolean; waiting: boolean }
export interface MyChannels {
  telegram: ChannelState; whatsapp: ChannelState; email: ChannelState;
  whatsapp_required: boolean; whatsapp_ready: boolean;
}
export interface LinkOut { code: string; wa_url?: string | null; qr_svg?: string | null; kaf_number?: string | null; expires_at: string; share_text: string }
export interface ConfirmOut { ok: boolean; message: string }
export interface TalentContactOut { phone?: string | null; email?: string | null; whatsapp: ChannelState; email_state: ChannelState; confirm_sent: boolean }
export interface ConnectionOut {
  configured: boolean; status: string; number?: string | null; name?: string | null; qr_image?: string | null;
  dry_run: boolean; sent_today: number; queued: number; daily_cap: number;
}
export interface ReachRow {
  kind: 'staff' | 'clinic' | 'doctor'; id: string; name: string; clinic?: string | null; role?: string | null;
  client_id?: string | null; telegram: ChannelState; whatsapp: ChannelState; email: ChannelState;
}
export interface LogRow {
  id: string; at: string; channel: 'in_app' | 'telegram' | 'whatsapp' | 'email'; direction: 'out' | 'in';
  person?: string | null; person_key?: string | null; person_kind?: string | null; clinic?: string | null;
  address?: string | null; kind?: string | null; title: string; body: string;
  creative_id?: string | null; creative_code?: string | null;
  status: string; error?: string | null; delivered_at?: string | null; read_at?: string | null;
}
export interface LogPage { rows: LogRow[]; total: number; counts: Record<string, number> }
export interface LogPerson { key: string; name: string; kind: string; clinic?: string | null }
export interface LogFilters {
  channel?: string; person?: string; creative?: string; kind?: string; status?: string; q?: string;
  since?: string; until?: string; direction?: string; limit?: number; offset?: number;
}
export interface ScriptPage { code: string; title: string; clinic: string; doctor: string; shoot: string; script: string; script_html?: string | null; state: 'waiting' | 'approved' | 'changes_requested' | 'replaced' }

export const fetchMyChannels = async () => (await api.get<MyChannels>('/connections/me')).data;
export const myWhatsAppLink = async () => (await api.post<LinkOut>('/connections/me/whatsapp-link')).data;
export const myWhatsAppUnlink = async () => (await api.delete<MyChannels>('/connections/me/whatsapp')).data;
export const setMyChannelPrefs = async (b: Partial<Record<'whatsapp' | 'telegram' | 'email', boolean>>) =>
  (await api.patch<MyChannels>('/connections/me/prefs', b)).data;
export const myEmailConfirm = async () => (await api.post<ConfirmOut>('/connections/me/email-confirm')).data;

export const userWhatsAppLink = async (id: string) => (await api.post<LinkOut>(`/connections/users/${id}/whatsapp-link`)).data;
export const userEmailConfirm = async (id: string) => (await api.post<ConfirmOut>(`/connections/users/${id}/email-confirm`)).data;

export const saveTalentContact = async (cid: string, tid: string, b: { phone?: string; email?: string }) =>
  (await api.put<TalentContactOut>(`/connections/clients/${cid}/talents/${tid}`, b)).data;
export const talentWhatsAppLink = async (cid: string, tid: string) =>
  (await api.post<LinkOut>(`/connections/clients/${cid}/talents/${tid}/whatsapp-link`)).data;
export const talentEmailConfirm = async (cid: string, tid: string) =>
  (await api.post<ConfirmOut>(`/connections/clients/${cid}/talents/${tid}/email-confirm`)).data;
export const talentWhatsAppUnlink = async (cid: string, tid: string) =>
  (await api.delete<TalentContactOut>(`/connections/clients/${cid}/talents/${tid}/whatsapp`)).data;

export const fetchWhatsAppConnection = async () => (await api.get<ConnectionOut>('/connections/whatsapp')).data;
export const whatsAppAction = async (a: 'start' | 'logout' | 'reset') => (await api.post<ConnectionOut>(`/connections/whatsapp/${a}`)).data;
export const fetchReachability = async () => (await api.get<ReachRow[]>('/connections/reachability')).data;
export const fetchMessageLog = async (f: LogFilters) => (await api.get<LogPage>('/connections/messages', { params: f })).data;
export const fetchLogPeople = async () => (await api.get<LogPerson[]>('/connections/messages/people')).data;

export const confirmEmail = async (token: string) => (await api.post<ConfirmOut>(`/confirm-email/${token}`)).data;
export const fetchScriptPage = async (token: string) => (await api.get<ScriptPage>(`/script-link/${token}`)).data;
export const decideScript = async (token: string, decision: 'approved' | 'changes_requested', feedback?: string) =>
  (await api.post<ScriptPage>(`/script-link/${token}`, { decision, feedback })).data;

export const errText = (e: unknown, fallback: string) => {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

/** The way a channel's state reads to a person. */
export function stateLabel(s: ChannelState): string {
  if (s.connected) return s.on ? 'Connected' : 'Connected, muted';
  if (s.waiting) return 'Waiting for them';
  return 'Not connected';
}

export interface GoogleOut {
  client_saved: boolean; client_id_hint?: string | null; connected: boolean; account?: string | null;
  connected_at?: string | null; last_error?: { at: string; message: string } | null;
  expected_account: string; redirect_uri: string;
}
export const fetchGoogle = async () => (await api.get<GoogleOut>('/connections/google')).data;
export const saveGoogleClient = async (client_id: string, client_secret: string) =>
  (await api.put<GoogleOut>('/connections/google/client', { client_id, client_secret })).data;
export const googleStartUrl = async () => (await api.get<{ url: string }>('/connections/google/start')).data.url;
export const googleDisconnect = async () => (await api.post<GoogleOut>('/connections/google/disconnect')).data;
export const googleTest = async () => (await api.post<ConfirmOut>('/connections/google/test')).data;
